import type { ReactNode } from 'react';
import { Link, useRouter } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { UserMenu } from '@/components/UserMenu';

/** Where the student came from, so the way out can be labelled with it. */
export type QuizOrigin = 'practice' | 'topic';

const ORIGIN_LABEL: Record<QuizOrigin, string> = {
  practice: 'Assessments',
  topic: 'Topic',
};

/**
 * The chrome for a quiz attempt.
 *
 * A quiz has its own URL, but it renders inside the topic lesson shell. From
 * the Assessments list that meant arriving to a LessonHeader for a topic you
 * did not ask about, a seven-tab lesson nav with NO tab active (the quiz route
 * is absent from tabLinks), and a second curriculum rail beside the global
 * sidebar — while losing the breadcrumb you came through. QuizRunner itself
 * had no Link, no navigate and no back affordance at all: once in, there was
 * no marked way out.
 *
 * So the route is exempted from AppLayout, the same treatment
 * /problems/$slug already gets, and this is what stands in its place.
 *
 * Deliberately has no notification bell. The problem workspace can carry one;
 * an attempt that auto-submits on timeout should not invite the student
 * somewhere else mid-question.
 */
export function QuizShell({
  title,
  origin,
  batchId,
  topicId,
  meta,
  actions,
  children,
}: {
  title: string;
  origin?: QuizOrigin;
  batchId: string;
  topicId: string;
  /** Timer, question counter — whatever the running engine wants to show. */
  meta?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  const router = useRouter();

  return (
    <div className="flex h-screen flex-col bg-background">
      <div className="flex h-12 shrink-0 items-center gap-2 border-b border-border bg-card px-2 sm:px-3">
        {origin === 'practice' ? (
          <Button asChild variant="ghost" size="sm" className="h-8 shrink-0 gap-1.5">
            <Link to="/courses/$batchId/practice" params={{ batchId }}>
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">{ORIGIN_LABEL.practice}</span>
            </Link>
          </Button>
        ) : origin === 'topic' ? (
          <Button asChild variant="ghost" size="sm" className="h-8 shrink-0 gap-1.5">
            <Link to="/courses/$batchId/topics/$topicId/practice" params={{ batchId, topicId }}>
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">{ORIGIN_LABEL.topic}</span>
            </Link>
          </Button>
        ) : (
          // No recorded origin — a shared link, or a refresh of an older URL.
          // History is the honest fallback; naming a destination we cannot
          // know would be a guess the student pays for.
          <Button
            variant="ghost"
            size="sm"
            className="h-8 shrink-0 gap-1.5"
            onClick={() => router.history.back()}
            aria-label="Go back"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Back</span>
          </Button>
        )}

        <div className="mx-1 h-5 w-px shrink-0 bg-border" aria-hidden="true" />

        <span className="truncate text-sm font-semibold text-foreground" title={title}>
          {title}
        </span>

        <div className="ml-auto flex shrink-0 items-center gap-2">
          {meta}
          {actions}
          <UserMenu size={26} showName={false} className="px-1 py-1" />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
    </div>
  );
}
